import React, { useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { formatted_user_role, formatted_user_status, user_role } from '@/entities/user'
import { useAuthStore } from '@/store/auth'
import { PLANS_DATA } from '@/constants/hotmart-plans'
import { X } from 'lucide-react'
import { AppliedFiltersState, useAppliedFilters } from '@/hooks/use-applied-filters'

type FilterDialogProps = {
  open: boolean
  close: () => void
  filters: AppliedFiltersState
  handleRemoveFilter: (key: string) => void
  handleApplyFilters: (filters: AppliedFiltersState) => void
  handleClearAllFilters: () => void
}

const loc = 'components/dialogs/filter-dialog'

export function FilterDialog({ open, close, filters, handleClearAllFilters, handleApplyFilters }: FilterDialogProps) {
  const { appliedFilters, setAppliedFilters } = useAppliedFilters()

  const { user } = useAuthStore()
  const handleChange = (key: string, value: string) => {
    setAppliedFilters({ ...appliedFilters, [key]: value })
  }

  const handleApply = () => {
    handleApplyFilters(appliedFilters)
  }

  const handleClear = () => {
    handleClearAllFilters()
  }

  useEffect(() => {
    setAppliedFilters(filters ?? appliedFilters)
  }, [])

  return (
    <>
      <AnimatePresence>
        {open && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 0.4 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black z-40" onClick={() => handleApply()} />
            <motion.div initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }} transition={{ type: 'spring', stiffness: 80 }} className="fixed right-0 top-0 h-full min-w-[400px] max-w-[400px] mdAndDown:min-w-[100%] bg-white shadow-2xl z-50 flex flex-col rounded-l-xl">
              <div className="flex items-center justify-between px-4 pb-2 border-b">
                <div className="flex flex-col items-start justify-between">
                  <h2 className="text-lg font-semibold">Filtros</h2>
                  <span className="text-sm font-thin">Selecione os filtros desejados</span>
                </div>
                <Button variant="outline" size="sm" onClick={() => close()} className="mt-2">
                  <X className="size-5" />
                </Button>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Busca</label>
                  <Input placeholder="Nome ou e-mail" value={appliedFilters?.search} onChange={e => handleChange('search', e.target.value)} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Função</label>
                  <Select value={appliedFilters.role} onValueChange={value => handleChange('role', value as user_role)}>
                    <SelectTrigger className="flex-1 rounded-lg truncate has-[span[data-placeholder]]:text-gray-100">
                      <SelectValue className="data-[placeholder]:text-gray-100" placeholder="Selecione a função" />
                    </SelectTrigger>
                    <SelectContent className="border-border truncate rounded-lg">
                      {Object.entries(formatted_user_role)
                        .filter(([value]) => (user?.role != user_role.ADMIN ? value != user_role.ADMIN : true))
                        .map(([value, text]) => (
                          <SelectItem key={value} value={value} className="rounded-lg text-sm truncate hover:cursor-pointer">
                            {text}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Plano</label>
                  <Select value={appliedFilters.planId} onValueChange={value => handleChange('planId', value)}>
                    <SelectTrigger className="flex-1 rounded-lg truncate has-[span[data-placeholder]]:text-gray-100">
                      <SelectValue className="data-[placeholder]:text-gray-100" placeholder="Selecione o plano" />
                    </SelectTrigger>
                    <SelectContent className="border-border truncate rounded-lg">
                      {PLANS_DATA.filter(plan => Number(plan?.id) !== 462895).map(plan => (
                        <SelectItem key={plan.id} value={plan.id.toString()} className="rounded-lg text-sm truncate hover:cursor-pointer">
                          {plan.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Status</label>
                  <Select value={appliedFilters.status} onValueChange={value => handleChange('status', value)}>
                    <SelectTrigger className="flex-1 rounded-lg truncate has-[span[data-placeholder]]:text-gray-100">
                      <SelectValue className="data-[placeholder]:text-gray-100" placeholder="Selecione o status" />
                    </SelectTrigger>
                    <SelectContent className="border-border truncate rounded-lg">
                      {Object.entries(formatted_user_status).map(([value, text]) => (
                        <SelectItem key={value} value={value} className="rounded-lg text-sm truncate hover:cursor-pointer">
                          {text}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Tipo de registro</label>
                  <Select value={appliedFilters.recordType} onValueChange={value => handleChange('recordType', value)}>
                    <SelectTrigger className="flex-1 rounded-lg truncate has-[span[data-placeholder]]:text-gray-100">
                      <SelectValue className="data-[placeholder]:text-gray-100" placeholder="Selecione o tipo de registro" />
                    </SelectTrigger>
                    <SelectContent className="border-border truncate rounded-lg">
                      <SelectItem value="hotmart" className="rounded-lg text-sm truncate hover:cursor-pointer">
                        Hotmart
                      </SelectItem>
                      <SelectItem value="local" className="rounded-lg text-sm truncate hover:cursor-pointer">
                        Manual
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex justify-end gap-2 p-4 border-t">
                <Button variant="outline" onClick={handleClear}>
                  Limpar
                </Button>
                <Button onClick={handleApply}>Aplicar</Button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  )
}
